import type { SoundfontSource } from "@midi-fantom-utils/player-core";
import { isSoundfontSource } from "@midi-fantom-utils/player-core";

declare const process: {
  env: {
    SOUNDFONT_URL?: string;
    SOUNDFONTS_URL?: string;
    APP_VERSION?: string;
    IS_DEV?: string;
  };
};

const OLD_STORAGE_KEY = "midi-fantom-utils/soundfont-source";
const STORAGE_KEY = "midi-fantom-utils/settings";

/**
 * Версия сборки, зашитая на этапе webpack (DefinePlugin) из
 * `process.env.APP_VERSION` CI — git describe + pipeline id. Для
 * локальной сборки без env — `"dev"`. Используется диагностикой и
 * `build-info.json` (см. webpack.config.cjs → BuildInfoPlugin).
 */
export const APP_VERSION = process.env.APP_VERSION || "dev";

/**
 * Признак dev-сборки, зашитый на этапе webpack (DefinePlugin) как
 * `process.env.IS_DEV = JSON.stringify(!isProd)` (dev = `webpack serve
 * --mode development`, prod = `webpack --mode production`). Используется
 * для визуального отличия плашки версии в dev от prod. В jsdom-тестах
 * DefinePlugin не работает — fallback `false` (ведём себя как prod).
 */
export const IS_DEV = process.env.IS_DEV === "true";

/**
 * Базовый URL внешнего статического каталога SoundFont
 * (https://soundfonts.nuware.ru — CORS `*`, immutable-кэш). От него
 * строятся `manifest.json` и ссылки на `.sf2`-файлы. Переопределяется
 * client-env `SOUNDFONTS_URL` (webpack.DefinePlugin), как `SOUNDFONT_URL`.
 */
export const DEFAULT_SOUNDFONTS_URL = "https://soundfonts.nuware.ru";

const readEnvCatalogUrl = (): string | undefined => {
  const value = process.env.SOUNDFONTS_URL;
  return typeof value === "string" && value.length > 0 ? value : undefined;
};

export const soundfontsBaseUrl = (): string =>
  readEnvCatalogUrl() ?? DEFAULT_SOUNDFONTS_URL;

/** URL manifest.json каталога SoundFont. */
export const SOUNDFONTS_MANIFEST_URL = `${soundfontsBaseUrl()}/manifest.json`;

/** URL `.sf2`-файла из каталога. */
export const soundfontCatalogUrl = (file: string): string =>
  `${soundfontsBaseUrl()}/${file}`;

/**
 * Дефолтный URL SF2. Экспортируется, чтобы любой fallback (первый mount
 * без сохранённых настроек, клик по radio «Ссылка (CDN)» с пустым
 * инпутом) использовал один и тот же источник.
 */
export const DEFAULT_SOUNDFONT_URL = `${DEFAULT_SOUNDFONTS_URL}/general-user-gs-v1.471.sf2`;

const readEnvUrl = (): string | undefined => {
  const value = process.env.SOUNDFONT_URL;
  return typeof value === "string" && value.length > 0 ? value : undefined;
};

const defaultSource = (): SoundfontSource => ({
  kind: "url",
  url: readEnvUrl() ?? DEFAULT_SOUNDFONT_URL,
});

/** Высота piano roll по умолчанию (px). */
export const DEFAULT_PIANO_ROLL_HEIGHT = 260;

/** Режим вывода звука: SF2 (браузерный синтез) или MIDI-порт (Fantom 06 / GM-синтезатор ОС). */
export type SoundOutput = "sf2" | "midi";

/** Выбранный MIDI-порт (id+name из Web MIDI; id может стать невалидным при переподключении). */
export type MidiPortSelection = {
  id: string;
  name: string;
};

/** Настройки внешнего вида таймлайна: только высота (px). Цвета — константы
 * `PIANO_ROLL_COLORS` в `pianoRollColors.ts`, из настроек убраны. */
export type PianoRollAppearance = {
  height: number;
};

export type Settings = {
  soundfont: SoundfontSource;
  soundOutput: SoundOutput;
  midiPort: MidiPortSelection | null;
  appearance: { pianoRoll: PianoRollAppearance };
};

const defaultAppearance = (): Settings["appearance"] => ({
  pianoRoll: {
    height: DEFAULT_PIANO_ROLL_HEIGHT,
  },
});

const isAppearance = (value: unknown): value is Settings["appearance"] => {
  if (!value || typeof value !== "object") return false;
  const v = value as { pianoRoll?: unknown };
  if (!v.pianoRoll || typeof v.pianoRoll !== "object") return false;
  const pr = v.pianoRoll as { height?: unknown };
  return typeof pr.height === "number" && Number.isFinite(pr.height);
};

export const DEFAULT_SETTINGS: Settings = {
  soundfont: defaultSource(),
  soundOutput: "sf2",
  midiPort: null,
  appearance: defaultAppearance(),
};

const isSoundOutput = (value: unknown): value is SoundOutput =>
  value === "sf2" || value === "midi";

const isMidiPortSelection = (value: unknown): value is MidiPortSelection => {
  if (value === null) return true;
  if (typeof value !== "object") return false;
  const v = value as { id?: unknown; name?: unknown };
  return typeof v.id === "string" && typeof v.name === "string";
};

const isSettings = (value: unknown): value is Settings => {
  if (!value || typeof value !== "object") return false;
  const v = value as {
    soundfont?: unknown;
    soundOutput?: unknown;
    midiPort?: unknown;
  };
  // Gate по soundfont (как раньше); новые поля `soundOutput`/`midiPort` —
  // опциональны при чтении (миграция старых настроек), но если присутствуют —
  // должны быть валидными, иначе весь объект отбрасывается.
  if (!isSoundfontSource(v.soundfont)) return false;
  if (v.soundOutput !== undefined && !isSoundOutput(v.soundOutput)) return false;
  if (v.midiPort !== undefined && !isMidiPortSelection(v.midiPort)) return false;
  return true;
};

/**
 * `kind: "file"` содержит blob:-URL, который становится мёртвым после
 * reload. Сохранять его в localStorage бесполезно (плеер не сможет
 * загрузить звук) и потенциально опасно (cross-tab утечка ссылки).
 * Поэтому при persist заменяем на дефолтный URL. `appearance`,
 * `soundOutput` и `midiPort` проходят сквозь невредимыми.
 */
const persistable = (s: Settings): Settings => {
  if (s.soundfont.kind === "file") {
    return { ...s, soundfont: defaultSource() };
  }
  return s;
};

/**
 * Гарантирует, что у настроек есть заполненные `appearance`, `soundOutput`
 * и `midiPort` (в т.ч. для объектов из localStorage, которые прошли
 * `isSettings` без них).
 */
const withAppearance = (s: {
  soundfont: SoundfontSource;
  soundOutput?: unknown;
  midiPort?: unknown;
  appearance?: unknown;
}): Settings => {
  const soundOutput = isSoundOutput(s.soundOutput) ? s.soundOutput : "sf2";
  const midiPort = isMidiPortSelection(s.midiPort) ? s.midiPort : null;
  const appearance = isAppearance(s.appearance)
    ? { pianoRoll: { height: s.appearance.pianoRoll.height } }
    : defaultAppearance();
  return { soundfont: s.soundfont, soundOutput, midiPort, appearance };
};

const tryRemove = (key: string): void => {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // ignore
  }
};

export const loadSettings = (): Settings => {
  if (typeof window === "undefined") return DEFAULT_SETTINGS;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as unknown;
      if (isSettings(parsed)) {
        // Старый ключ мигрировали в прошлой сессии, но если по какой-то
        // причине он остался — удаляем его сейчас, чтобы не накапливать
        // мёртвые ключи.
        tryRemove(OLD_STORAGE_KEY);
        return withAppearance(persistable(parsed));
      }
    }
    // Миграция со старого ключа — одноразово.
    const legacy = window.localStorage.getItem(OLD_STORAGE_KEY);
    if (legacy) {
      const parsed = JSON.parse(legacy) as unknown;
      if (isSoundfontSource(parsed)) {
        const migrated: Settings = withAppearance({ soundfont: parsed });
        try {
          window.localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify(persistable(migrated))
          );
          window.localStorage.removeItem(OLD_STORAGE_KEY);
        } catch {
          // ignore — не критично
        }
        return migrated;
      }
    }
  } catch {
    // localStorage недоступен / повреждён — падаем на дефолт.
  }
  return DEFAULT_SETTINGS;
};

export const saveSettings = (settings: Settings): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(persistable(withAppearance(settings)))
    );
  } catch {
    // ignore — некритично.
  }
};
